<template>
  <div>
    <v-data-table
      :headers="headers"
      disable-filtering
      disable-pagination
      disable-sort
      hide-default-footer
      no-data-text=""
    >
      <template v-slot:body.prepend>
        <tr>
          <td>
            <v-menu
              ref="menuDateTo"
              v-model="menuDateTo"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="dateTo | periodFormat"
                  :label="$t('DATA_TABLE.FILTER')"
                  append-icon="mdi-calendar"
                  clearable
                  readonly
                  role="button"
                  autocomplete="nope"
                  @click:clear="clear_to"
                  v-on="on"
                />
              </template>
              <v-date-picker
                ref="picker"
                v-model="dateTo"
                type="month"
                min="1940-01-01"
                @change="saveDateto"
              />
            </v-menu>
          </td>

          <td>
            <v-autocomplete
              v-model="filter.customer"
              :label="$t('DATA_TABLE.FILTER')"
              :search-input.sync="customer"
              :items="customers"
              :loading="loading"
              item-text="name"
              item-value="name"
              :no-data-text="$t('TASKS.NO_DATA_FILTER_TEXT')"
              clearable
            />
          </td>

          <td>
            <v-autocomplete
              v-model="filter.building_complexes"
              :label="$t('DATA_TABLE.FILTER')"
              :search-input.sync="building_complex"
              :items="buildingComplexes"
              :loading="loading"
              item-text="name"
              item-value="id"
              :no-data-text="$t('TASKS.NO_DATA_FILTER_TEXT')"
              clearable
              multiple
              chips
              small-chips
              deletable-chips
              @change="building_complex = ''"
            />
          </td>

          <td>
            <v-select
              v-model="filter.types"
              :items="invoiceTypes"
              item-text="name"
              item-value="id"
              :label="$t('DATA_TABLE.FILTER')"
              :loading="loading"
              chips
              small-chips
              multiple
              clearable
            >
              <template v-slot:selection="data">
                <v-chip
                  v-bind="data.attrs"
                  :input-value="data.selected"
                  close
                  @click="data.select"
                  @click:close="remove(filter.types, data.item)"
                >
                  {{ data.item.name }}
                </v-chip>
              </template>
            </v-select>
          </td>

          <td>
            <v-autocomplete
              v-model="filter.invoice_number"
              :label="$t('DATA_TABLE.FILTER')"
              :search-input.sync="invoice_number"
              :items="invoice_numbers"
              :loading="loading"
              item-text="name"
              item-value="name"
              :no-data-text="$t('TASKS.NO_DATA_FILTER_TEXT')"
              clearable
            />
          </td>

          <td>
            <v-select
              v-model="filter.statuses"
              :items="invoiceStatuses"
              item-text="name"
              item-value="id"
              :label="$t('DATA_TABLE.FILTER')"
              :loading="loading"
              chips
              small-chips
              multiple
              clearable
            >
              <template v-slot:selection="data">
                <v-chip
                  v-bind="data.attrs"
                  :input-value="data.selected"
                  close
                  @click="data.select"
                  @click:close="remove(filter.statuses, data.item)"
                >
                  {{ data.item.name }}
                </v-chip>
              </template>
            </v-select>
          </td>

          <td>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn color="grey" depressed outlined @click="reset" v-on="on">
                  <v-icon>mdi-filter-remove</v-icon>
                </v-btn></template
              >
              <span>{{ $t('BUTTON.CLEAR_FILTER') }}</span>
            </v-tooltip>
            <v-spacer />
          </td>

          <td>
            <v-btn color="primary" depressed @click.prevent="refreshData">
              {{ $t('BUTTON.FIND') }}
            </v-btn>
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { periodFormat } from '@/modules/_global/filters';
import { debounce, cloneDeep, isEmpty } from 'lodash';
import { mapCacheActions } from 'vuex-cache';

export default {
  name: 'InvoiceListFilter',

  filters: {
    periodFormat
  },

  data: () => ({
    unwatch: null,
    options: {},
    delay: 0,
    invoiceTypes: [],
    invoiceStatuses: [],
    invoice_number: null,
    customer: null,
    building_complex: null,

    invoice_numbers: [],
    customers: [],
    buildingComplexes: [],

    filter: {},

    menuDateTo: false,
    dateTo: null,

    expensiveFunc: null
  }),

  computed: {
    ...mapState(['loading']),
    ...mapGetters('ereporter', { prevParams: 'INVOICES_FILTER' }),

    debouncedRequest() {
      return debounce(this.expensiveFunc, this.delay);
    },
    headers() {
      return [
        {
          text: this.$t('EREPORTER.PERIOD'),
          sortable: false
        },
        {
          text: this.$t('ORDERS.CUSTOMER'),
          sortable: false
        },
        {
          text: this.$t('BUILDING_COMPLEXES.ITEM'),
          sortable: false
        },
        {
          text: this.$t('ITEM.TYPE'),
          sortable: false
        },
        {
          text: this.$t('EREPORTER.INVOICES.INVOICE_NUMBER'),
          sortable: false
        },
        {
          text: this.$t('EMPLOYEES.STATUS'),
          sortable: false
        },
        {
          text: '',
          sortable: false
        },
        {
          text: '',
          sortable: false
        }
      ];
    },
    types() {
      return [
        { id: 0, name: this.$t('ORDERS.TYPES.ALL') },
        { id: 1, name: this.$t('EREPORTER.S') },
        { id: 2, name: this.$t('EREPORTER.M') }
      ];
    }
  },

  watch: {
    dateTo(val) {
      if (val) {
        this.filter.period = val;
      } else {
        this.filter.period = null;
      }
    },

    customer(val) {
      if (val !== this.filter.customer && !isEmpty(val)) {
        this.expensiveFunc = this.getDataCustomers;
        this.debouncedRequest();
      }
    },

    invoice_number(val) {
      if (val !== this.filter.invoice_number && !isEmpty(val)) {
        this.expensiveFunc = this.getDataInvoiceNumber;
        this.debouncedRequest();
      }
    }
  },

  created() {
    if (!isEmpty(this.prevParams)) {
      this.filter = cloneDeep(this.prevParams);
      this.customer = this.prevParams.customer;
      this.getDataCustomers();
      this.invoice_number = this.filter.invoice_number;
      this.getDataInvoiceNumber();
      this.dateTo = this.filter.period;
    }

    this.refreshData();
    this.setData();
    this.unwatch = this.$store.watch(
      state => state.localization.lang,
      () => {
        const lang = this.$store.state.localization.lang;

        if (lang) {
          this.refreshData();
          this.setData();
        }
      }
    );
    this.setDelay(500);
  },

  destroyed() {
    this.unwatch();
  },

  methods: {
    ...mapActions('ereporter', [
      'getInvoiceTypes',
      'getFilteredInvoiceNumbers',
      'getFilteredInvoiceCustomers'
    ]),
    ...mapCacheActions('ereporter', ['getInvoiceStatuses']),
    ...mapCacheActions('building_complex', ['retrieveBuildingComplexesList']),

    clear_to() {
      this.dateTo = null;
    },

    saveDateto(date) {
      this.$refs.menuDateTo.save(date);
    },

    expensiveOperation: function() {
      return this.debouncedRequest();
    },
    refreshData() {
      this.$emit('setRequest', cloneDeep(this.filter));
    },
    setDelay(v) {
      this.delay = v;
    },
    setData() {
      this.getInvoiceTypes()
        .then(({ data }) => {
          this.invoiceTypes = data.data;
        })
        .catch(error => {
          this.error = error;
        });

      this.getInvoiceStatuses()
        .then(({ data }) => {
          this.invoiceStatuses = data.data;
        })
        .catch(error => {
          this.error = error;
        });

      this.retrieveBuildingComplexesList()
        .then(({ data }) => {
          this.buildingComplexes = data.data;
        })
        .catch(error => {
          this.error = error;
        });
    },
    getDataCustomers() {
      this.getFilteredInvoiceCustomers({ search: this.customer })
        .then(({ data }) => {
          this.customers = data.data;
        })
        .catch(error => {
          this.error = error;
        });
    },
    getDataInvoiceNumber() {
      this.getFilteredInvoiceNumbers({ search: this.invoice_number })
        .then(({ data }) => {
          this.invoice_numbers = data.data;
        })
        .catch(error => {
          this.error = error;
        });
    },
    remove(filer, item) {
      const index = filer.indexOf(item.id);

      if (index >= 0) {
        filer.splice(index, 1);
      }
    },
    reset() {
      this.customer = null;
      this.invoice_number = null;
      this.customer = null;
      this.filter = {};
      this.menuDateFrom = false;
      this.dateFrom = null;
      this.menuDateTo = false;
      this.dateTo = null;
      this.expensiveFunc = null;

      this.refreshData();
    }
  }
};
</script>
